/* Web Bluetooth scanner — graceful degradation.
 * Works: Chrome/Edge/Opera on Desktop + Android.
 * Doesn't work: iOS Safari (Apple blocks Web Bluetooth entirely).
 * Cannot detect: AirPods, AirTags (Apple's private MAC rotation hides them).
 */
import React, { useState } from 'react';
import { Bluetooth, BluetoothOff, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

function isSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

export default function BluetoothScanner() {
  const [devices, setDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const supported = isSupported();

  const scan = async () => {
    if (!supported) return;
    setScanning(true);
    try {
      // Request any nearby BLE device. User must pick one from the OS chooser.
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ['battery_service'],
      });
      const entry = {
        id: device.id,
        name: device.name || '(okänd enhet)',
        connected: false,
        battery: null,
        at: new Date().toISOString(),
      };
      // Try to read battery_service (works on some headphones / wearables)
      try {
        const server = await device.gatt.connect();
        entry.connected = true;
        try {
          const svc = await server.getPrimaryService('battery_service');
          const ch = await svc.getCharacteristic('battery_level');
          const val = await ch.readValue();
          entry.battery = val.getUint8(0);
        } catch {/* not all devices expose battery */}
        device.gatt.disconnect();
      } catch {/* not connectable */}

      setDevices((prev) => [entry, ...prev.filter((d) => d.id !== entry.id)].slice(0, 10));
      toast.success(`Hittade: ${entry.name}${entry.battery != null ? ` (${entry.battery}% batteri)` : ''}`);
    } catch (e) {
      if (e?.name !== 'NotFoundError') {
        toast.error('Skanningen avbröts');
      }
    } finally {
      setScanning(false);
    }
  };

  if (!supported) {
    return (
      <div className="neo-card p-3 bg-[#FFE8F5]" data-testid="bt-scanner-unsupported">
        <div className="flex items-start gap-2">
          <BluetoothOff className="w-5 h-5 text-[#D06892] shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold">Bluetooth-skanning funkar inte här 😢</p>
            <p className="opacity-70 mt-0.5">
              Webbläsare på iPhone (Safari) blockerar all Bluetooth-skanning.
              Använd <b>Hitta</b>-appen för AirTags/AirPods.
            </p>
            <p className="opacity-60 italic mt-1">
              💡 På Chrome/Edge på dator eller Android funkar skannern.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="neo-card p-3 bg-white space-y-2" data-testid="bt-scanner">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-['Fredoka'] font-bold text-sm flex items-center gap-1">
          <Bluetooth className="w-4 h-4 text-[#4A8BC7]" /> Bluetooth-skanner
        </h4>
        <button
          onClick={scan}
          disabled={scanning}
          className="neo-btn px-3 py-1.5 bg-[#A2D2FF] text-xs font-bold"
          data-testid="bt-scan-btn"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Skanna efter enheter'}
        </button>
      </div>
      <p className="text-[10px] opacity-60 italic flex items-center gap-1">
        <Info className="w-3 h-3" /> AirPods och AirTags syns INTE (Apple döljer dem)
      </p>
      {devices.length > 0 && (
        <ul className="space-y-1" data-testid="bt-devices-list">
          {devices.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-xs p-2 rounded bg-[#FFF8FB] border border-[#E8D5E5]" data-testid={`bt-device-${d.id}`}>
              <span className="text-lg">📱</span>
              <span className="font-bold flex-1 truncate">{d.name}</span>
              {d.battery != null && (
                <span className="text-[10px] font-bold opacity-70">🔋 {d.battery}%</span>
              )}
              {d.connected && <span className="text-[9px] text-green-600 font-bold">●</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
